import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy, ComponentFactoryResolver } from '@angular/core';
// Service
import { BroadcastService, ModalService, ModalSize, ObjectUtils, ToasterType } from '@cv-invision/core';
import { CVfUtilsService } from '@cv-invision/cv.invision-views.ux';
import { ViewsService } from '@cv-invision/cv.invision-views.ux';
import { LayoutRenderService } from '@cv-invision/cv.invision-views.ux';
import { ExternalApiService } from '@cv-invision/cv.invision-views.ux';
import { SmartTagsService } from '../smart-tags/services/smart-tags.service';
import { PhotoGalleryService } from '@cv-invision/cv.component-library.ux';
// Model
import { BroadcastEventModel } from '@cv-invision/core';
import { LayoutRenderLayoutItem } from '@cv-invision/cv.invision-views.ux';
import { PhotoGalleryOptions } from '../../models/components.options';
import { FORM_EVENT, SCHEMA_TYPE } from '../../../../../models/enums.model';
import { RENDERERS } from '../smart-tags/model';
import { GeneralEventType, TabsEventType } from '../../models/component.events';
import { GalleryOptions, ImageGallery, PhotoGalleryEvents } from '@cv-invision/cv.component-library.ux';
// Other
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { RotateImageComponent } from "../rotate-image/rotate-image.component";
import { DrawFormsComponent } from "../draw-forms/draw-forms.component";

@Component({
	selector: 'cv-schema-render-photo-gallery',
	templateUrl: './photo-gallery.component.html',
	providers: [ PhotoGalleryService ]
})
export class PhotoGalleryComponent implements OnInit, OnChanges, OnDestroy {

	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	markForCheck: boolean;
	options = new PhotoGalleryOptions();

	thisData: any;
	photos: any;
	path: string | null;

	customCssClass = [ 'gallery-with-descr' ];
	galleryOptions: GalleryOptions = {
		noFilesImage: 'assets/img/placeholder/nofiles.png',
		hasSelected: true,
		showDescription: true,
		showCounters: true,
		showPreview: true,
		size: {
			width: '100%',
			height: '225px'
		},
		customActions: [
			{
				icon: 'fas fa-sync',
				eventName: PhotoGalleryEvents.ROTATE_IMAGE
			},
			{
				icon: 'fas fa-pen-paintbrush',
				eventName: PhotoGalleryEvents.DRAW_FORMS
			}
		]
	}

	smartTagTypes: any = [];
	album: ImageGallery[] = [];
	currentAlbumImage: any;
	currentIndexAlbumSelected = 0;
	previewPhoto: any = null;

	showLoading = true;
	noImage = true;
	imageErrorCounter = 0;

	get order(): any {
		return this.viewsService.order;
	}

	get flattenedPhotos(): any {
		return this.viewsService.flattenedPhotos;
	}

	get photosSelected(): any {
		return this.viewsService.photosSelected;
	}

	get cssClass(): string {
		return this.options.showDescription ? this.customCssClass.join(' ') : '';
	};

	get isEditable(): boolean {
		return this.layoutRenderService.activateEditableUX;
	}

	get title(): string {
		let title = '';
		if ('readDescriptionFromData' in this.options) {
			if (Array.isArray(this.options.readDescriptionFromData.key)) {
				const dataPieces: any = [];
				this.options.readDescriptionFromData.key.forEach(datapointKey => {
					dataPieces.push(this.thisData[datapointKey]);
				});
				title = dataPieces.join(this.options.readDescriptionFromData.separator || ' | ');
			} else {
				title = this.thisData[this.options.readDescriptionFromData.key] || '';
			}
		}
		return title;
	}

	protected interval: number;
	protected atComponentDestroy = new Subject();

	constructor(
		private broadcastService: BroadcastService,
		private modalService: ModalService,
		private viewsService: ViewsService,
		private layoutRenderService: LayoutRenderService,
		private cvfUtils: CVfUtilsService,
		private externalApiService: ExternalApiService,
		private smartTagsService: SmartTagsService,
		private photoGalleryService: PhotoGalleryService,
		protected componentFactoryResolver: ComponentFactoryResolver,
	) {
		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT_ALL_NONE)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.album.forEach(image => {
					let photoindex = -1;
					if (image) {
						photoindex = this.photosSelected.indexOf(image.id);
					}

					if (photoindex > -1) {
						image.selected = true;
					} else {
						image.selected = false;
					}
				});
				this.photoGalleryService.events.next({
					name: PhotoGalleryEvents.CHANGE_IMAGE,
					index: this.currentIndexAlbumSelected
				});
				this.changeImage({index: this.currentIndexAlbumSelected}, true);
			});

		this.photoGalleryService
			.events
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				this.currentAlbumImage = this.album[event.index];
				this.currentIndexAlbumSelected = event.index;

				switch (event.name) {
					case PhotoGalleryEvents.SELECT_IMAGE:
						this.selectImage();
						break;
					case PhotoGalleryEvents.DELETE_IMAGE:
						this.deleteImage();
						break;
					case PhotoGalleryEvents.ROTATE_IMAGE:
						this.openRotateImage();
						break;
					case PhotoGalleryEvents.DRAW_FORMS:
						this.openDrawForms();
						break;
				}
			});
	}

	async ngOnInit(): Promise<void> {
		await this.initializeComponent();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.data.isFirstChange()) {
			if (this.options.photosInData) {
				this.thisData = changes.data.currentValue.data;
				this.photos = changes.data.currentValue.photos;
			} else {
				this.thisData = changes.data.currentValue.data;
				this.photos = this.flattenedPhotos[this.path] || [];
			}
			this.galleryOptions.size.width = this.options.size?.width;
			this.galleryOptions.size.height = this.options.size?.height;
			this.loadImages();
		}
	}

	ngOnDestroy(): void {
		if (this.interval) {
			clearInterval(this.interval);
		}
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Initialize the Component
	 * @private
	 */
	public async initializeComponent(): Promise<void> {
		this.options = {...this.options, ...this.layoutNode.options};
		this.smartTagTypes = this.layoutRenderService.getExtraDataOption('smartTagTypes') || [];

		if (this.data) {
			if (this.options.photosInData) {
				this.thisData = this.data.data;
				this.photos = this.data.photos;
				this.path = this.getNPath();
			} else {
				// calculate photos given the path in data
				this.thisData = this.data;
				this.path = this.getNPath();
				this.photos = this.flattenedPhotos[this.path] || [];
			}

			this.galleryOptions.size.width = this.options.size?.width;
			this.galleryOptions.size.height = this.options.size?.height;
			await this.loadImages();
		}
	}

	/**
	 * Change image Callback
	 * @param data {index: int, img: string}
	 */
	changeImage(data?: any, shouldTriggerEvent?: boolean): void {
		this.currentIndexAlbumSelected = data.index;
		this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
		this.previewPhoto = this.currentAlbumImage.medium;

		if (shouldTriggerEvent) {
			this.layoutRenderService.componentBroadcast.next({
				eventType: TabsEventType.UPDATE_TABS,
				jsonPath: this.path
			});
		}
	}

	/**
	 * initialize the image variable with the images for certain section.
	 */
	async loadImages(): Promise<void> {
		this.album = [];
		let images = this.photos || [];

		if (this.thisData && this.thisData.length > 0) {
			images = [];
			this.thisData.forEach((item: any, index: any) => {
				const itemPath = `${item._Path}`;
				const itemImages = this.photos.filter((item: any) => item.refJsonPath === itemPath) || [];

				let imgToBePushed;
				let title = '';

				if (this.options.renderTemplate) {
					let renderer = new RENDERERS.TagDefault;
					const rendererName = 'rendererKey' in this.options.renderTemplate ?
						item[this.options.renderTemplate.rendererKey] :
						this.options.renderTemplate.rendererName;

					const tagType = this.cvfUtils.getSmartTagItem(this.smartTagTypes, rendererName);

					// To match with a default tag
					let tagTypeRenderer: any = 'TagDefault';

					if (tagType && tagType.smartTagType.viewMode in RENDERERS) {
						tagTypeRenderer = tagType.smartTagType.viewMode;
					}

					if (rendererName === 'Scan Comment') {
						tagTypeRenderer = 'Scan Comment';
					}

					renderer = new RENDERERS[tagTypeRenderer];
					title = renderer.render(item);

					if (renderer.cssClass) {
						this.customCssClass.push(renderer.cssClass);
					}
				}

				if (title === '') {
					title = item.description;
				}

				if (itemImages && itemImages.length > 0) {
					imgToBePushed = itemImages[0];

					if (title === '') {
						title = imgToBePushed.metadata.description;
					}

					imgToBePushed.metadata.description = title;
					images.push(imgToBePushed);
				}
			});
		}

		if (images && images.length) {
			this.noImage = false;

			images.forEach((img: any) => {
				this.addImageToAlbum(img, this.title);
			});
			this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
			this.changeImage({index: 0});
		}
	}

	/**
	 * event to go back to previous photo
	 */
	previousPanorama(): void {
		if (this.currentIndexAlbumSelected === 0) {
			this.currentIndexAlbumSelected = this.album.length - 1;
		} else {
			this.currentIndexAlbumSelected--;
		}

		this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
		this.changeImage({index: this.currentIndexAlbumSelected})
	}

	/**
	 * callback to go to next photo
	 */
	nextPanorama(): void {
		this.currentIndexAlbumSelected++;
		if (this.album.length === this.currentIndexAlbumSelected) {
			this.currentIndexAlbumSelected = 0;
		}
		this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
		this.changeImage({index: this.currentIndexAlbumSelected})
	}

	/**
	 * callback to display confirmation modal to delete photo
	 */
	deleteImage(): void {
		this.modalService
			.customConfirm(
				'Delete Image',
				`Do you really want to delete it? There is no undo operation`,
				'icon-warning fas fa-exclamation-circle',
				'Delete',
				'cv-btn-link-error'
			)
			.subscribe(async result => {
				if (result.confirm === 'confirm') {
					await this.deleteImageFromGallery(this.currentAlbumImage);
				}
			});
	}

	/**
	 * callback to select photo
	 */
	selectImage(): void {
		const photoIndex = this.photosSelected.indexOf(this.currentAlbumImage.id);
		if (photoIndex > -1) {
			this.photosSelected.splice(photoIndex, 1);
		} else {
			this.photosSelected.push(this.currentAlbumImage.id);
		}
		this.album[this.currentIndexAlbumSelected].selected = !this.album[this.currentIndexAlbumSelected].selected;
		this.changeImage({index: this.currentIndexAlbumSelected}, true);
	}

	/**
	 * Add image object to class variable album
	 * @param img
	 */
	private addImageToAlbum(img: any, title?: string): void {
		if (img) {
			let description = img.metadata.description || '';
			if (title && title !== '') {
				description = title;
			}

			if (img.metadata.aiProvider) {
				description = `${description} | Provider: ${img.metadata.aiProvider}`;
			}

			let photoindex = -1;

			if (img) {
				photoindex = this.photosSelected.indexOf(img.id);
			}

			const image = {
				id: img.id,
				big: img.path,
				src: img.path,
				thumb: img.thumbnails[0],
				small: img.thumbnails[0],
				medium: img.thumbnails[0],
				imageDescription: description,
				description: '',
				showDelete: this.order.schema === SCHEMA_TYPE.CVDS_40 ? true : img.custom,
				refJsonPath: img.refJsonPath,
				selected: photoindex >= 0,
				isSmartTag: false,
				smartTagObjectId: 0,
				boundingBoxes: img.boundingBoxes,
			};

			// if it is an SmartTag
			if (img.refJsonPath.includes('smartTags')) {
				const smartTag = this.thisData.find((item: any) => item._Path === img.refJsonPath);
				// Only if it an 3.4 smartTag (the ones that we can delete)
				if (smartTag) {
					image['isSmartTag'] = true;
					image['smartTagObjectId'] = smartTag.smartTagObjectId;
				}
			}
			this.album.push(image);
		}
	}

	/**
	 * Deletes the image from the Gallery, locally and in the server
	 * @param photoSelected
	 * @private
	 */
	private async deleteImageFromGallery(photoSelected: any): Promise<void> {
		let response = null;

		// NEEDs REFACTOR
		// Delete it from Smart Tg
		if (photoSelected.isSmartTag) {
			// Delete Attachment and the SmartTag
			//response = await this.externalApiService.deleteSmartTagById(this.order.id, photoSelected.smartTagObjectId).toPromise();
			response = await this.smartTagsService.deleteSmartTag(photoSelected, 'v1');
			//response = {deleted: true};
		} else {
			// Delete it from attachment only
			response = await this.externalApiService.deleteAttachmentById(this.order.id, photoSelected.id).toPromise();

			this.broadcastService.broadcast({
				name: ToasterType.TOASTER,
				event: {
					type: 'warning',
					title: 'Attachment deleted',
					message: 'Attachment was deleted'
				}
			});
			// response = {deleted: true};
		}

		if (response && response !== null && response.deleted) {
			// Not Really Necessary but force Angular to redraw the gallery
			const albumPhotosIndex = this.album.findIndex((attachment: any) => {
				return attachment.id === photoSelected.id;
			});

			if (this.album.length >= 0) {
				this.album.splice(albumPhotosIndex, 1);
				this.photoGalleryService.events.next({name: PhotoGalleryEvents.IMAGE_DELETED, index: albumPhotosIndex});
			}

			if (!photoSelected.isSmartTag) {
				// Delete it from the Flattened Photos
				const flattenedPhotosIndex = this.viewsService.flattenedPhotos[photoSelected.refJsonPath].findIndex((attachment: any) => {
					return attachment.id === photoSelected.id;
				});
				if (flattenedPhotosIndex > -1) {
					this.viewsService.flattenedPhotos[photoSelected.refJsonPath].splice(flattenedPhotosIndex, 1);
				}

				// Delete it from View Services Photos
				const viewServicePhotosIndex = this.viewsService.photos.findIndex((attachment: any) => {
					return attachment.id === photoSelected.id;
				});
				if (viewServicePhotosIndex > -1) {
					this.viewsService.photos.splice(viewServicePhotosIndex, 1);
				}
			}

			// Delete it from Selected photo if exists
			const photoSelectedIndex = this.photosSelected.findIndex((item: any) => {
				return item === photoSelected.id;
			});
			if (photoSelectedIndex > -1) {
				this.photosSelected.splice(photoSelectedIndex, 1);
			}

			// No images, jump to 3D Tour
			if (this.album.length === 0) {
				this.layoutRenderService.componentBroadcast.next({
					eventType: TabsEventType.GET_ACTIVE_TAB,
					jsonPath: this.path,
					metaData: {
						reloadData: true
					}
				});
			} else {
				if (this.currentIndexAlbumSelected !== 0) {
					this.previousPanorama();
				}
				this.changeImage({index: this.currentIndexAlbumSelected}, true);
			}

			this.layoutRenderService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: this.path,
				metaData: response
			});
		}
	}

	/**
	 * doc must exist
	 *
	 * @returns
	 */
	private getNPath(): string {
		let nPath;

		if (this.thisData) {
			if ('_Path' in this.thisData) {
				nPath = this.thisData._Path;
			} else if (Array.isArray(this.thisData)) {
				nPath = this.thisData[0]._Path;
			}

			// NEEDs REFACTOR
			if (nPath) {
				nPath = nPath.split('.smartTags')[0];
			}
		}

		return nPath;
	}

	/**
	 * Event to open modal to rotate image
	 * @private
	 */
	private openRotateImage(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: RotateImageComponent,
				data: {
					jsonPath: this.path,
					currentAlbumImage: this.currentAlbumImage,
					photos: this.photos,
					order: this.order
				},
				modalConfiguration: {
					title: `Rotate Image`,
					modalSize: ModalSize.LG
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (result) => {
				if (result.id) {
					const photoSelectedIndex = this.photos.findIndex(item => item.id === result.id);
					if (photoSelectedIndex >= 0) {
						const newPhotoAlbum = ObjectUtils.recursiveDeepCopy(this.photos[photoSelectedIndex])
						newPhotoAlbum.path = result.path;
						newPhotoAlbum.thumbnails = result.thumbnails;
						newPhotoAlbum['forceReload'] = Date.now();
						this.photos.splice(photoSelectedIndex, 1, newPhotoAlbum);
						await this.loadImages();
					}

				}
			});
	}

	/**
	 * Event to open modal to draw forms in images for smart tags
	 * @private
	 */
	openDrawForms(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: DrawFormsComponent,
				data: {
					jsonPath: this.path,
					currentAlbumImage: this.currentAlbumImage,
					photos: this.photos,
					order: this.order
				},
				modalConfiguration: {
					modalCustomClass: 'blur-modal',
					title: `Apply Blur`,
					modalSize: ModalSize.XXL,
					titleIcon: 'far fa-pen-paintbrush'
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (result) => {
				if (result.id) {
					const photoSelectedIndex = this.photos.findIndex(item => item.id === result.id);
					if (photoSelectedIndex >= 0) {
						const newPhotoAlbum = ObjectUtils.recursiveDeepCopy(this.photos[photoSelectedIndex])
						newPhotoAlbum.path = result.path;
						newPhotoAlbum.thumbnails = result.thumbnails;
						newPhotoAlbum['forceReload'] = Date.now();
						this.photos.splice(photoSelectedIndex, 1, newPhotoAlbum);
						await this.loadImages();
					}

				}
			});
	}

}
